import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// One icon per destination, none shared: the rail is scanned by shape. Lucide,
// the family ds/ uses.
import {
  Boxes,
  Building2,
  ChartColumn,
  Cloud,
  CloudCog,
  Container,
  Database,
  Earth,
  FileSearch,
  FileTerminal,
  Fingerprint,
  Globe,
  Group,
  HardDrive,
  History,
  IdCard,
  KeyRound,
  LayoutDashboard,
  Monitor,
  Network,
  Rocket,
  ScrollText,
  Server,
  Settings,
  ShieldCheck,
  SquareTerminal,
  UsersRound,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { AppSidebar, AppHeader, type AppSidebarItem, type AppSidebarSection } from '@/components/ds-zta';
import { useThemeMode } from '@/lib/theme';
import { brandAsset, useBrand } from '@/brand';
import GlobalTopLoader from './GlobalTopLoader';
import { useRailTooltip, useSmoothHoverPill } from './railTooltip';
import { usePermissions } from '../hooks/usePermissions';
import { useUser } from '../contexts/UserContext';
import { useTour } from '../tours/TourContext';
import { getTour } from '../tours/registry';
import { authApi } from '../services/api';
import LicenseBanner from './LicenseBanner';

// The guided tour each route runs. The header's help button starts (or resumes)
// the tour for the current page; on a page with none it opens the Getting Started
// page, which lists every tour.
const ROUTE_TOURS: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/ssh-resources': 'ssh',
  '/k8s-resources': 'kubernetes',
  '/resources': 'database',
  '/desktop-resources': 'desktop',
  '/proxy-resources': 'proxy',
  '/cloud-providers': 'cloud',
};

interface NavItem {
  name: string;
  href?: string;
  icon: LucideIcon;
  /**
   * Browser-tab text when this page is open. Nested children use short labels
   * that read well indented under their group ("AWS"), which is too vague on its
   * own in a tab bar; set this to override.
   */
  pageTitle?: string;
  children?: NavItem[];
}

interface NavSection {
  id: string;
  /** Rendered as an uppercase heading; hidden while the rail is collapsed. */
  title?: string;
  items: NavItem[];
}

// The 23 destinations, in nested groups (cascade plan 3.2). A group's "Overview"
// child is the group's own landing page.
const navigation: NavSection[] = [
  {
    id: 'overview',
    items: [{ name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }],
  },
  {
    id: 'access',
    title: 'Access',
    items: [
      { name: 'All resources', href: '/resources', icon: Server, pageTitle: 'Resources' },
      {
        name: 'Resources',
        icon: Boxes,
        children: [
          { name: 'SSH', href: '/ssh-resources', icon: SquareTerminal, pageTitle: 'SSH servers' },
          { name: 'Kubernetes', href: '/k8s-resources', icon: Container, pageTitle: 'Kubernetes' },
          { name: 'Databases', href: '/databases', icon: Database },
          { name: 'Desktop', href: '/desktop-resources', icon: Monitor, pageTitle: 'Desktop access' },
          { name: 'Proxy', href: '/proxy-resources', icon: Globe, pageTitle: 'Proxy resources' },
        ],
      },
      {
        name: 'Cloud providers',
        icon: Cloud,
        children: [
          { name: 'Overview', href: '/cloud-providers', icon: CloudCog },
          { name: 'AWS', href: '/cloud-providers/aws', icon: Warehouse },
          { name: 'Azure', href: '/cloud-providers/azure', icon: Building2 },
          { name: 'GCP', href: '/cloud-providers/gcp', icon: Earth },
          { name: 'Hetzner', href: '/cloud-providers/hetzner', icon: HardDrive },
        ],
      },
      { name: 'Access requests', href: '/access-requests', icon: KeyRound },
      { name: 'Certificates', href: '/certificates', icon: ShieldCheck },
    ],
  },
  {
    id: 'administration',
    title: 'Administration',
    items: [
      {
        name: 'Identity',
        icon: Fingerprint,
        children: [
          { name: 'Users', href: '/users', icon: UsersRound },
          { name: 'Groups', href: '/groups', icon: Group },
          { name: 'Roles', href: '/roles', icon: IdCard },
          { name: 'Policies', href: '/policies', icon: ScrollText },
        ],
      },
      {
        name: 'Audit',
        icon: FileSearch,
        children: [
          { name: 'Access logs', href: '/access-logs', icon: ChartColumn },
          { name: 'SSH activity', href: '/ssh-activity-logs', icon: FileTerminal, pageTitle: 'SSH activity logs' },
          { name: 'Sessions', href: '/sessions', icon: History },
        ],
      },
      { name: 'Trusted clusters', href: '/trusted-clusters', icon: Network },
      { name: 'Settings', href: '/settings', icon: Settings },
      { name: 'Getting started', href: '/setup-guide', icon: Rocket },
    ],
  },
];

const SIDEBAR_COLLAPSED_KEY = 'zta_sidebar_collapsed';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const brand = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccessRoute } = usePermissions();
  const { userInfo, activeRole, isLoading: isLoadingPermissions } = useUser();
  const { startTour, resumeTour, getStatus } = useTour();
  // Resolved light/dark, live: follows an OS switch under "system".
  const { resolvedMode } = useThemeMode();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Persisted across reloads, as d2d-XDR's rail is. Storage can be blocked
  // (private mode); the rail then simply starts expanded.
  const [collapsed, setCollapsedState] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const setCollapsed = (next: boolean) => {
    setCollapsedState(next);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
    } catch {
      /* not remembered this time */
    }
  };
  // Collapsed rail: each icon's name pops out beside the rail, and the hover
  // pill glides between rows (d2d-XDR bafce6c, via d2d-UEM).
  const railTip = useRailTooltip(collapsed, true);
  useSmoothHoverPill(railTip.ref, true);

  // Close the mobile drawer once a nav link has navigated.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // An entry shows when its route may be opened: canAccessRoute, the same check
  // the router's RequireRoute applies, so the nav and a typed URL agree (F27).
  // Admin-only routes are listed once, in utils/permissions ADMIN_ONLY_ROUTES.
  const hasAccess = (item: NavItem): boolean => !item.href || canAccessRoute(item.href);

  // Exactly one nav link is current: the LONGEST href that matches the path, so
  // /cloud-providers/aws lights AWS, not the group's Overview.
  const matches = (href: string) => location.pathname === href || location.pathname.startsWith(href + '/');
  const currentHref = navigation
    .flatMap((section) => section.items.flatMap((item) => [item.href, ...(item.children ?? []).map((c) => c.href)]))
    .filter((href): href is string => !!href && matches(href))
    .sort((a, b) => b.length - a.length)[0];
  const isActive = (href?: string) => !!href && href === currentHref;

  // Browser tab title: "<page> · <brand>", from the nav entry that is current.
  const tabTitle = (() => {
    for (const section of navigation) {
      for (const item of section.items) {
        if (isActive(item.href)) return item.pageTitle ?? item.name;
        for (const child of item.children ?? []) {
          if (!isActive(child.href)) continue;
          return child.name === 'Overview' ? item.name : child.pageTitle ?? child.name;
        }
      }
    }
    return null;
  })();

  useEffect(() => {
    document.title = tabTitle ? `${tabTitle} · ${brand.name}` : brand.name;
  }, [tabTitle, brand.name]);

  const renderLink = (props: {
    href: string;
    'aria-current'?: 'page';
    className?: string;
    children: ReactNode;
  }): ReactElement => {
    const { href, children: linkChildren, ...rest } = props;
    return (
      <Link to={href} {...rest}>
        {linkChildren}
      </Link>
    );
  };

  // Group ids are prefixed with the section id so a label reused across
  // sections can't collide on AppSidebar's expand/collapse keys.
  const buildItems = (sectionId: string, items: NavItem[]): AppSidebarItem[] => {
    const result: AppSidebarItem[] = [];
    for (const item of items) {
      if (!hasAccess(item)) continue;
      if (item.children) {
        const children = item.children.filter(hasAccess).map((child) => ({
          id: `${sectionId}/${item.name}/${child.name}`,
          label: child.name,
          icon: <child.icon size={20} />,
          href: child.href,
          active: isActive(child.href),
        }));
        if (children.length === 0) continue;
        result.push({
          id: `${sectionId}/${item.name}`,
          label: item.name,
          icon: <item.icon size={20} />,
          children,
          active: children.some((c) => c.active),
        });
      } else {
        result.push({
          id: `${sectionId}/${item.name}`,
          label: item.name,
          icon: <item.icon size={20} />,
          href: item.href,
          active: isActive(item.href),
        });
      }
    }
    return result;
  };

  // Until the profile has loaded, the role is unknown and so is the nav; the rail
  // stays empty for that moment rather than showing links a role then removes.
  // A section whose every entry is hidden is dropped whole.
  const sections: AppSidebarSection[] = isLoadingPermissions
    ? []
    : navigation
        .map((section) => ({
          id: section.id,
          ...(section.title ? { title: section.title } : {}),
          items: buildItems(section.id, section.items),
        }))
        .filter((section) => section.items.length > 0);

  // Help: the tour for this page, resumed where it was left; otherwise the
  // Getting Started page and its list of every tour. The guide is open to every
  // role, so help lands somewhere useful on any page; the check stays because a
  // deployment can still withhold the route.
  const pageTour = ROUTE_TOURS[location.pathname];
  const canOpenGuide = !isLoadingPermissions && canAccessRoute('/setup-guide');
  const onHelp = () => {
    const tourId = pageTour;
    const tour = tourId ? getTour(tourId) : undefined;
    if (!tourId || !tour || tour.comingSoon || tour.steps.length === 0) {
      if (canOpenGuide) navigate('/setup-guide');
      return;
    }
    const status = getStatus(tourId);
    if (status === 'in_progress' || status === 'paused') resumeTour(tourId);
    else startTour(tourId);
  };

  const userName = userInfo?.name || userInfo?.email || (isLoadingPermissions ? '' : 'User');

  // The app shell on the d2d design-system components, laid out as d2d-XDR's
  // AppFrame: the rail in flow beside a column that holds the header and the one
  // scrolling region. Below lg the rail slides in over the page from the header's
  // menu button. The account menu holds the role, the theme switcher and sign-out.
  return (
    // overflow-clip, not overflow-hidden: a hidden box is still scrollable by
    // script, so any scrollIntoView (a table of contents, a tour step) scrolled
    // the whole shell up and left a blank band under it. A clipped box cannot scroll.
    <div className="flex h-[100dvh] overflow-clip bg-[var(--d2d-bg-primary)]">
      {/* Skip link (eval F43): the first tab stop, hidden until focused, so a
          keyboard user can jump past the rail and the header to the page. */}
      <a
        href="#main-content"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main-content')?.focus();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[10000] focus:rounded-md focus:bg-[var(--d2d-bg-card)] focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-[var(--d2d-text-primary)] focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[var(--ds-color-ring)]"
      >
        Skip to main content
      </a>
      <GlobalTopLoader />

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-[var(--overlay-scrim)] lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={
          'fixed inset-y-0 left-0 z-50 transition-transform duration-300 lg:static lg:z-auto lg:translate-x-0 motion-reduce:transition-none ' +
          (sidebarOpen ? 'translate-x-0' : '-translate-x-full')
        }
      >
        <AppSidebar
          ref={railTip.ref}
          aria-label="Main navigation"
          theme={resolvedMode}
          brand={{
            logo: <img src={brandAsset(brand.mark)} alt="" aria-hidden="true" className="rc-sidenav__brand-mark" />,
            title: (
              // d2d Logo Lockup: the wordmark is Britti Sans 500 at line-height 1.0.
              <span className="truncate font-display text-heading-sm font-medium leading-none text-[var(--d2d-text-primary)]">
                {brand.name}
              </span>
            ),
            href: '/dashboard',
          }}
          sections={sections}
          renderLink={renderLink}
          collapsed={collapsed}
          onCollapseChange={setCollapsed}
        />
        {railTip.tip}
      </aside>

      <div id="app-main-col" className="flex min-w-0 flex-1 flex-col">
        <AppHeader
          showSearch={false}
          showNotifications={false}
          showHelp={!!pageTour || canOpenGuide}
          helpLabel={pageTour ? 'Take the tour of this page' : 'Getting started and tours'}
          onHelpClick={onHelp}
          showThemeSwitcher={true}
          user={userName ? { name: userName, role: activeRole?.name } : undefined}
          showProfile={false}
          onLogout={() => authApi.logout()}
          onToggleSidebar={() => setSidebarOpen(true)}
        />

        {/* #app-scroll is the one scrolling region: the tours scroll it to bring a
            step's target into view, and the screenshot tool measures it. relative:
            the containing block for anything absolutely positioned in a page. */}
        <main id="main-content" tabIndex={-1} className="relative min-h-0 min-w-0 flex-1 focus:outline-none">
          {/* pt-2 at every size: an 8px gap between the app header and the page
              title. PageHeader's tint band (ds.css .rc-page-header::before) reaches
              16px up, so it still meets the app header's hairline; the part above
              the scroller is clipped by its overflow. Do not go above pt-4, or a
              strip of page background opens between the header and the band. */}
          <div
            id="app-scroll"
            className="h-full overflow-y-auto px-4 pt-2 pb-4 text-[var(--d2d-text-primary)] sm:px-6 sm:pb-6 lg:px-8 lg:pb-8"
          >
            {/* The licence, for everyone, above every page (F25). */}
            <LicenseBanner />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
